import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { AlertifyService } from '../../services/alertify.service';

@Component({
    selector: 'or-system-alerts-manager',
    templateUrl: './system-alerts-manager.component.html',
    styleUrls: ['./system-alerts-manager.component.scss'],
    standalone: false
})
export class SystemAlertsManagerComponent implements OnInit {

  @Input() public set rule(data) {
    this._rule = data;

    if (this._rule.ruleType == 'field-metrics') {
      this.metricTypes = this.fieldMetricTypes;

      // Field metrics use the same role-based scopes as quality metrics —
      // include "I am the OA" after Secondary PM on the OpinionRoute instance.
      if (this.auth.isORInstance()) {
        this.scopeTypes = [
          {label: 'I am the PM', type: 'PM'},
          {label: 'I am the Secondary PM', type: 'SecondaryPM'},
          {label: 'I am the OA', type: 'OA'},
          {label: 'a PM on my team', type: 'Team'}
        ];
      }
    }
    else if (this._rule.ruleType == 'tasks') {

      this.scopeTypes = [
        {label: 'I am the PM', type: 'PM'},
        {label: 'I am the Secondary PM', type: 'SecondaryPM'},
        {label: 'the PM is on my team', type: 'Team'}
      ];

      this.metricTypes = [{label: 'Overdue', type:'OverdueTasksAssignedToMe'}, {label:'Assigned to me', type:'TasksAssignedToMe'}];
      this.alertMethods = ['Email', 'Slack'];
      this.selectedMetric = 'Select';
    }
    else if (this._rule.ruleType == 'past-due') {

      this.scopeTypes = [
        {label: 'My Projects', type: 'MyProjects'},
        {label: 'My Team\'s Projects', type: 'Team'}
      ];
    }
    else if (this._rule.ruleType == 'survey-activity') {

      this.scopeTypes = [
        {label: 'My Projects', type: 'MyProjects'},
        {label: 'My Team\'s Projects', type: 'Team'}
      ];
      this.alertMethods = ['Email', 'SMS text message', 'Slack'];

      // For new (unsaved) alerts, initialise recipient row unselected.
      // loadData() handles existing / copied rules.
      if (!this._rule.id && !this._rule.copy) {
        this.listRecipients = [{
          value: 'Select user',
          type: null,
          selected: false,
          options: this.baseRecipientOptions()
        }];
      }
    }
    else if (this.auth.isORInstance()) {
      // Quality metrics — include "I am the OA" after Secondary PM on the OpinionRoute instance
      this.scopeTypes = [
        {label: 'I am the PM', type: 'PM'},
        {label: 'I am the Secondary PM', type: 'SecondaryPM'},
        {label: 'I am the OA', type: 'OA'},
        {label: 'a PM on my team', type: 'Team'}
      ];
    }

    this.loadData();
  }

  @Input() alertHistory: Boolean = false; //used when displaying alert history modal
  @Input() surveySettings: any;

  @Output() conditionChanged = new EventEmitter();
  @ViewChild('ddScope', {static: false}) ddScope;

  _rule;

  scopeTypes = [
    {label: 'I am the PM', type: 'PM'},
    {label: 'I am the Secondary PM', type: 'SecondaryPM'},
    {label: 'a PM on my team', type: 'Team'}
  ]

  metricTypes = [
    {label: 'Manual QCs', type: 'ManualQCs'},
    {label: 'DAR', type: 'DAR'},
    {label: 'CID Dupes', type: 'CIDDupes'},
    {label: 'CID Fraud', type: 'CIDFraud'},
    {label: 'Nav OQs', type: 'NavOQs'}
  ];

  fieldMetricTypes = [
    {label: 'LOI', type: 'LOI'},
    {label: 'IR', type: 'IR'},
    {label: 'DOR', type: 'DOR'}
  ];

  alertMethods = ['Email', 'SMS text message', 'Slack'];
  operators = [{label: 'Above', value: '>'}, {label: 'Below', value: '<'}];

  surveyStatusOptions = [
    {label: 'Soft Launch', type: 'SoftLaunch'},
    {label: 'Full Launch', type: 'FullLaunch'}
  ];
  respondentStatusOptions = [
    {label: 'Nav Entries', type: 'NavEntries'},
    {label: 'Starts',      type: 'Starts'},
    {label: 'Completes',   type: 'Completes'},
    {label: 'Terminates',  type: 'Terminates'},
    {label: 'OQs',         type: 'OQs'},
    {label: 'Auto QCs',    type: 'AutoQCs'},
    {label: 'Manual QCs',  type: 'ManualQCs'}
  ];
  selectedSurveyStatuses: string[] = [];
  selectedRespondentStatus = 'Select respondent status';
  selectedDays: number = 0;

  selectedScope = 'Select group';
  selectedMetric = 'Select metric';
  selectedMetricNumber: any = {label: '0', value: 0, suffix: ''};
  selectedOperator = 'Select';
  selectedAlertMethod = 'Select method';
  selectedAlertMethodLabel = '';

  addingEmail = false;
  newRecipient = '';
  canAddNewRecipient = false;
  listRecipients: any[] = [{selectedValue: 'Select user', type: '', selected:false}];

  _projectTeam:any = [];
  anyWithoutSlackUsername = false;

  integerPattern = { 9: { pattern: new RegExp('^[0-9]*$'), optional: true } };
  emailValidator = new FormControl('', [Validators.required, Validators.email]);

  constructor(
    private auth: AuthService,
    private elRef: ElementRef,
    private alertify: AlertifyService) { }

  // Base recipient options; includes Operations Associate on the OpinionRoute instance
  private baseRecipientOptions(): { label: string }[] {
    const options: { label: string }[] = [{ label: 'Me' }, { label: 'Project Manager' }, { label: 'Secondary PM' }];
    if (this.auth.isORInstance()) {
      options.push({ label: 'Operations Associate' });
    }
    return options;
  }

  ngOnInit(): void { }

  loadData() {

    if (this._rule.id || this._rule.copy) {
      this.selectedScope = this.scopeTypes.find(x => x.type === this._rule.scopeType)?.label || 'Select group';
      this.selectedMetric = this.metricTypes.find(x => x.type === this._rule.rules[0].metricType)?.label || 'Select metric';
      this.selectedMetricNumber = {label: this._rule.rules[0].value, value: this._rule.rules[0].value, suffix: '%'};
      this.selectedAlertMethod = this._rule.ruleActions[0].alertMethod;
      this.selectedAlertMethodLabel = this.formatAlertMethodLabel(this.selectedAlertMethod);

      // handle recipients and merge the default options with the values from DB
      this.listRecipients = this._rule.ruleActions[0].alertRecipientType?.split(',').map(item => ({ value: item, type: item.includes('@') ? 'email' : null, selected: true }));

      const defaultOptions = this.baseRecipientOptions();

      this.listRecipients = this._rule.ruleActions[0].alertRecipientType?.split(',').map((item) => {
        const type = item.includes('@') ? 'email' : null;
        const options = [...defaultOptions, { label: item }].filter((option, index, self) => self.findIndex((o) => o.label === option.label) === index);
        return { value: item, type, selected: true, options };
      }) || [];

      this.handleSelectedRecipients(false);

      this.selectedOperator = this.operators.find(x => x.value ===  this._rule.rules[0].operator)?.label || 'Select';
      this.selectedMetricNumber.suffix = (this.selectedMetric !== 'Nav OQs' ? '%' : '');

      // Restore survey-activity specific fields
      if (this._rule.ruleType === 'survey-activity') {
        this.selectedSurveyStatuses = this._rule.surveyStatus
          ? this._rule.surveyStatus.split(',').filter(Boolean)
          : [];
        const foundRespondent = this.respondentStatusOptions.find(x => x.type === this._rule.rules[0].metricType);
        this.selectedRespondentStatus = foundRespondent ? foundRespondent.label : 'Select respondent status';
        this.selectedDays = this._rule.rules[0].value || null;
      }
    }
  }

  formatAlertMethodLabel(alertMethodString) {
    if (!alertMethodString) {
      return '';
    }

    // Split the comma-separated string and trim whitespace
    const alertMethods = alertMethodString.split(',').map(method => method.trim());

    if (alertMethods.length === 1) {
      return alertMethods[0];
    } else if (alertMethods.length === 2) {
      return `${alertMethods[0]} and ${alertMethods[1]}`;
    } else {
      const lastMethod = alertMethods[alertMethods.length - 1];
      const middleMethods = alertMethods.slice(1, -1).join(', ');
      return `${alertMethods[0]}, ${middleMethods} and ${lastMethod}`;
    }
  }

  applyScope(newValue) {
    this.selectedScope = newValue.label;
    this.ddScope.hide();
    this.conditionChanged.emit({condition: 'scope', newValue});
  }

  applyMetric(newValue) {

    this.selectedMetric = newValue.label;
    this.selectedMetricNumber.suffix = (this.selectedMetric !== 'Nav OQs' ? '%' : '');
    this.selectedMetricNumber.label = 0;
    this.selectedOperator = 'Select';
    this.operators = [{label: 'Above', value: '>'}, {label: 'Below', value: '<'}];

    if (this._rule.ruleType == 'tasks') {
      this.alertMethods = ['Email', 'Slack'];
    } else {
      this.alertMethods = ['Email', 'SMS text message', 'Slack'];
      this.selectedAlertMethod = 'Select method';
    }

    switch (this.selectedMetric) {
      case 'LOI':
      case 'IR':
        break;
      case 'Manual QCs':
        this.selectedMetricNumber.label = this.surveySettings?.dropoutRate || 0;
        break;
      case 'DAR':
        this.selectedMetricNumber.label = 80;
        this.selectedOperator = 'Below';
        break;
      case 'DOR':
        this.selectedMetricNumber.label = this.surveySettings?.dropoutRate || 0;
        this.selectedOperator = 'Above';
        break;
      case 'CID Dupes':
        this.selectedMetricNumber.label = 5;
        this.selectedOperator = 'Above';
        break;
      case 'CID Fraud':
        this.selectedMetricNumber.label = this.surveySettings?.cidFraudRate || 0;
        this.selectedOperator = 'Above';
        break;
      case 'Nav OQs':
        this.selectedMetricNumber.label = 0;
        this.selectedOperator = null;
        break;
    }

    this.applyMetricNumber();
    this.conditionChanged.emit({condition: 'metric', newValue});

    const operator = this.operators.find(x => x.label === this.selectedOperator)?.value || null;
    this.conditionChanged.emit({condition: 'operator', newValue: {value: operator} });
  }

  applyMetricNumber() {
    let newValue = this.selectedMetricNumber;
    newValue.value = newValue.label;
    this.conditionChanged.emit({condition: 'metric-number', newValue});
  }

  applyOperator(newValue) {
    this.selectedOperator = newValue.label;
    this.conditionChanged.emit({condition: 'operator', newValue});
  }

  applyAlertMethod(newValue, checked) {

    let phone = this.auth.getUser().phone;
    let slackUsername = this.auth.getUser().social.slackUsername;

    // Parse current comma-separated string into a Set
    const selections = new Set(
      (this.selectedAlertMethod || '').split(',').map(s => s.trim()).filter(Boolean)
    );

    if (checked) {
      if (newValue === 'SMS text message' && !phone) {
        this.alertify.error('There is no Mobile in your profile');
        checked = false;
      } else if (newValue === 'Slack' && !slackUsername) {
        this.alertify.error('There is no Slack Username in your profile');
        checked = false;
      }
    }

    // Apply change
    if (checked) {
      selections.add(newValue);
    } else {
      selections.delete(newValue);
    }

    // Preserve order if alertMethods exists
    const ordered = this.alertMethods?.length
      ? this.alertMethods.filter((m: string) => selections.has(m))
      : Array.from(selections);

    // If none selected, set placeholder
    if (ordered.length === 0) {
      this.selectedAlertMethod = 'Select method';
    } else {
      this.selectedAlertMethod = ordered.join(', ');
      this.selectedAlertMethodLabel = this.formatAlertMethodLabel(this.selectedAlertMethod);
    }

    // Emit updated value
    this.conditionChanged.emit({
      condition: 'alertMethod',
      newValue: this.selectedAlertMethod
    });

  }

  isMethodSelected(method: string): boolean {
    if (!this.selectedAlertMethod) return false;
    return this.selectedAlertMethod.split(',').map(m => m.trim()).includes(method);
  }

  handleFields(type) {
    if (type === 'number') {
      this.applyMetricNumber();
    }
  }

  checkEmail(event) {
    this.newRecipient = event.target.value;
    this.emailValidator.setValue(this.newRecipient.trim());
  }

  cancelRecipientEmail() {
    this.addingEmail = false;
    this.newRecipient = '';
  }

   addNewRecipient() {

    const existingRecipients = new Set(
      this.listRecipients
        .filter(recipient => recipient.selected && recipient.value !== 'Select user')
        .map(recipient => recipient.value)
    );

    const newOptions = this.baseRecipientOptions();
    const filteredOptions = newOptions.filter(option => !existingRecipients.has(option.label));

    // Add new recipient with filtered options
    this.listRecipients.push({
      value: 'Select user',
      type: null,
      selected: false,
      options: [...filteredOptions],
    });

    this.canAddNewRecipient = false;
  }

  addNewRecipientEmail(index) {
    const dropdown = this.listRecipients[index];
    const email = this.newRecipient.trim();

    if (!email) {
      this.alertify.error("Please enter email.");
      return;
    }

    const exist = this.listRecipients.some(item => item?.selected && item?.value.trim() === email);
    if (exist) {
      this.alertify.error("E-mail already registered.");
      this.newRecipient = '';
      return;
    }

    this.alertMethods = ['Email'];
    this.selectedAlertMethod = 'Email';

    dropdown.options.push({ label: email });
    dropdown.value = email;
    dropdown.type = 'email';
    dropdown.selected = true;

    this.conditionChanged.emit({condition: 'alertMethod', newValue: "Email"});
    this.handleSelectedRecipients();
    this.newRecipient = '';
    this.addingEmail = false;
  }

  applyAlertRecipient(recipient, selected) {
    let canAdd = true;

    const exist = this.listRecipients.find(x => x.selected && x.value === selected?.label);
    if (exist) {
      this.alertify.error("Recipient already added.");
      canAdd = false;
      recipient.value = 'Select user';
      recipient.type = null;
      recipient.selected = false;
    }

    if (canAdd)  {
      recipient.value = selected?.label.trim();
      recipient.selected = true;
      this.handleSelectedRecipients();
    }

    this.handleAlertMethods();
  }

  removeNewRecipient(index) {
    const removedValue = this.listRecipients[index]?.value !== 'Select user' ? this.listRecipients[index].value : null;
    this.listRecipients.splice(index, 1);

    if (this.listRecipients.length === 0) {
      this.addNewRecipient();
      this.selectedAlertMethod = 'Select method';
    }
    else if (removedValue) {
      this.listRecipients.forEach(recipient => {
        if (!recipient.options.some(option => option.label === removedValue)) {
          recipient.options.push({ label: removedValue });
        }
      });
    }
    this.handleAlertMethods();
    this.handleSelectedRecipients();
  }

  handleAlertMethods() {
    const anyPMSelected = this.listRecipients.some(x => x.selected && (x.value === 'Project Manager' || x.value === 'Secondary PM'));
    const anyEmailSelected = this.listRecipients.some(x => x.selected && x.value.includes('@'));

    if (anyPMSelected) {
      this.conditionChanged.emit({condition: 'alertMethod', newValue: "Email"});
      this.alertMethods = ['Email', 'Slack'];
      if (!this.alertMethods.includes(this.selectedAlertMethod))
        this.selectedAlertMethod = 'Select method';
    }
    else {
      this.alertMethods = ['Email', 'SMS text message', 'Slack'];
    }

    if (anyEmailSelected) {
      this.selectedAlertMethod = 'Email';
      this.alertMethods = ['Email'];
      this.conditionChanged.emit({condition: 'alertMethod', newValue: "Email"});
    }
  }

  handleSelectedRecipients(emit = true) {
    this.canAddNewRecipient = this.listRecipients.filter(x => !x?.selected).length === 0;
    const newValue = this.listRecipients.filter(x => x?.selected).map(x => x?.value).join(',');
    if (emit) this.conditionChanged.emit({condition: 'alertRecipient', newValue, canAddRecipient: this.canAddNewRecipient});

    // Recipient-driven scope coupling only applies to the MyProjects/Team scope model.
    // Metrics and tasks alerts use role-based scopes (PM / Secondary PM / OA / Team),
    // so leave their scope list and selection untouched.
    if (this._rule.ruleType === 'metrics' || this._rule.ruleType == null || this._rule.ruleType === 'tasks' || this._rule.ruleType === 'field-metrics') {
      return;
    }

    // Hide 'my teams projects' if 'me' is not the only user selected)
    if (this.listRecipients.length > 0) {
      const onlyMeSelected = this.listRecipients.every(x => x?.selected && x.value === 'Me');

      if (onlyMeSelected) {
        this.scopeTypes = [
          {label: 'My Projects', type: 'MyProjects'},
          {label: 'My Team\'s Projects', type: 'Team'}
        ];
      } else {
        this.scopeTypes = [{label: 'My Projects', type: 'MyProjects'}];
        this.selectedScope = 'My Projects';
        this.conditionChanged.emit({condition: 'scope', newValue: {type: 'MyProjects'}});
      }
    }
  }

  // ── Survey-Activity helpers ──────────────────────────────────────────────

  get selectedSurveyStatusLabel(): string {
    if (this.selectedSurveyStatuses.length === 0) return 'Select survey status';
    const labels = this.selectedSurveyStatuses
      .map(type => this.surveyStatusOptions.find(s => s.type === type)?.label || type);
    return labels.length === 1 ? labels[0] : labels.join(' or ');
  }

  isSurveyStatusSelected(type: string): boolean {
    return this.selectedSurveyStatuses.includes(type);
  }

  applySurveyStatus(option, checked: boolean) {
    if (checked) {
      if (!this.selectedSurveyStatuses.includes(option.type)) {
        this.selectedSurveyStatuses = [...this.selectedSurveyStatuses, option.type];
      }
    } else {
      this.selectedSurveyStatuses = this.selectedSurveyStatuses.filter(s => s !== option.type);
    }
    this.conditionChanged.emit({ condition: 'surveyStatus', newValue: this.selectedSurveyStatuses.join(',') });
  }

  applyRespondentStatus(option) {
    this.selectedRespondentStatus = option.label;
    this.conditionChanged.emit({ condition: 'respondentStatus', newValue: option.type });
  }

  applyDaysInactive() {
    this.conditionChanged.emit({ condition: 'daysInactive', newValue: this.selectedDays });
  }

  // ────────────────────────────────────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedElement = event.target as HTMLElement;
    if (!clickedElement.classList.contains('clickable')) {
      const editingDiv = this.elRef.nativeElement.querySelector('.grid-alert');
      const isClickInside = editingDiv && editingDiv.contains(clickedElement);
      if (!isClickInside) {
        this.listRecipients = this.listRecipients.filter(x => x.selected);
        this.handleSelectedRecipients();
      }
    }
  }

}
